(() => {
  const ROOT_SELECTOR = "[data-footer-storefront-map-root]";
  const GOOGLE_MAPS_API_URL = "https://maps.googleapis.com/maps/api/js";
  const GEOCODE_TIMEOUT_MS = 7000;

  let googleMapsApiPromise = null;

  const FALLBACK_PALETTES = {
    light: {
      bg: "#FAFAFA",
      text: "#0F1F3A",
      muted: "#40516E",
      accent: "#003595",
      surface: "#F3F7FF",
      border: "#C8D6F0",
    },
    dark: {
      bg: "#020816",
      text: "#EAF4FF",
      muted: "#A9C1DA",
      accent: "#2D7FFF",
      surface: "#091328",
      border: "#1B3763",
    },
  };

  function toHexChannel(value) {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return null;
    const channel = Math.max(0, Math.min(255, Math.round(number)));
    return channel.toString(16).padStart(2, "0").toUpperCase();
  }

  function normalizeColor(value, fallback) {
    const color = (value || "").trim();
    if (!color) return fallback;

    if (/^#[0-9a-f]{6}$/i.test(color)) {
      return color.toUpperCase();
    }

    if (/^#[0-9a-f]{3}$/i.test(color)) {
      return `#${color
        .slice(1)
        .split("")
        .map((character) => `${character}${character}`)
        .join("")
        .toUpperCase()}`;
    }

    const channels = color.match(/[\d.]+/g);
    if (channels && channels.length >= 3) {
      const red = toHexChannel(channels[0]);
      const green = toHexChannel(channels[1]);
      const blue = toHexChannel(channels[2]);
      if (red && green && blue) return `#${red}${green}${blue}`;
    }

    return fallback;
  }

  function readPaletteColor(styles, scheme, token, fallback) {
    return normalizeColor(styles.getPropertyValue(`--brand-${scheme}-${token}`), fallback);
  }

  function getThemeScheme() {
    return document.documentElement.dataset.themeScheme === "dark" ? "dark" : "light";
  }

  function getActivePalette() {
    const scheme = getThemeScheme();
    const fallback = FALLBACK_PALETTES[scheme];
    const styles = getComputedStyle(document.documentElement);

    return {
      scheme,
      bg: readPaletteColor(styles, scheme, "bg", fallback.bg),
      text: readPaletteColor(styles, scheme, "text", fallback.text),
      muted: readPaletteColor(styles, scheme, "muted", fallback.muted),
      accent: readPaletteColor(styles, scheme, "accent", fallback.accent),
      surface: readPaletteColor(styles, scheme, "surface", fallback.surface),
      border: readPaletteColor(styles, scheme, "border", fallback.border),
    };
  }

  function buildMapStyles(palette) {
    return [
      {
        elementType: "geometry",
        stylers: [{ color: palette.bg }],
      },
      {
        elementType: "labels.text.fill",
        stylers: [{ color: palette.text }],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [{ color: palette.surface }, { weight: 3 }],
      },
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: palette.border }],
      },
      {
        featureType: "landscape.man_made",
        elementType: "geometry",
        stylers: [{ color: palette.surface }],
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: palette.surface }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: palette.muted }],
      },
      {
        featureType: "poi.business",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: palette.surface }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: palette.surface }],
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: palette.border }],
      },
      {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [{ color: palette.border }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: palette.accent }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: palette.text }],
      },
      {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [{ color: palette.muted }],
      },
      {
        featureType: "transit",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: palette.border }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: palette.accent }],
      },
    ];
  }

  function buildMarkerIcon(googleMaps, palette = getActivePalette()) {
    return {
      path: googleMaps.SymbolPath.CIRCLE,
      fillColor: palette.accent,
      fillOpacity: 1,
      strokeColor: palette.bg,
      strokeOpacity: 1,
      strokeWeight: 3,
      scale: 8,
    };
  }

  function applyMapTheme(map, marker, googleMaps) {
    const palette = getActivePalette();

    map.setOptions({
      styles: buildMapStyles(palette),
      backgroundColor: palette.bg,
    });

    if (marker) {
      marker.setIcon(buildMarkerIcon(googleMaps, palette));
    }
  }

  function loadGoogleMapsApi(apiKey) {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("Google Maps can only load in the browser"));
    }

    if (window.google?.maps) {
      return Promise.resolve(window.google.maps);
    }

    if (googleMapsApiPromise) {
      return googleMapsApiPromise;
    }

    googleMapsApiPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        "[data-footer-google-maps-script], [data-store-ship-google-maps-script], script[src*='maps.googleapis.com/maps/api/js']",
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.google.maps), {
          once: true,
        });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Google Maps could not load")),
          { once: true },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = `${GOOGLE_MAPS_API_URL}?key=${encodeURIComponent(apiKey)}&v=weekly`;
      script.async = true;
      script.defer = true;
      script.dataset.footerGoogleMapsScript = "true";
      script.dataset.storeShipGoogleMapsScript = "true";
      script.addEventListener("load", () => resolve(window.google.maps), { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("Google Maps could not load")),
        { once: true },
      );
      document.head.appendChild(script);
    });

    return googleMapsApiPromise;
  }

  function readMapConfig(root) {
    const configNode = root.querySelector("[data-footer-storefront-map-config]");
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || "{}");
    } catch (error) {
      console.error("Footer storefront map config is invalid", error);
      return null;
    }
  }

  function geocodeAddress(googleMaps, address) {
    return new Promise((resolve, reject) => {
      const geocoder = new googleMaps.Geocoder();
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("Geocoding timed out"));
      }, GEOCODE_TIMEOUT_MS);

      geocoder.geocode({ address }, (results, status) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);

        if (status !== "OK" || !results?.[0]?.geometry?.location) {
          reject(new Error(`Geocoding failed with status ${status}`));
          return;
        }

        resolve(results[0]);
      });
    });
  }

  function showMapFallback(root, shouldShowFallback) {
    const shell = root.querySelector("[data-footer-storefront-map-shell]");
    const fallback = root.querySelector("[data-footer-storefront-map-fallback]");

    if (shell) {
      shell.hidden = shouldShowFallback;
    }

    if (fallback) {
      fallback.hidden = !shouldShowFallback;
    }
  }

  function guardRenderedMap(root, canvas) {
    window.setTimeout(() => {
      if (!canvas.querySelector(".gm-style") && canvas.children.length === 0) {
        showMapFallback(root, true);
      }
    }, 2500);
  }

  async function hydrateFooterMap(root) {
    const config = readMapConfig(root);
    const canvas = root.querySelector("[data-footer-storefront-map-canvas]");

    if (!config || !canvas || !config.apiKey || !config.address) {
      showMapFallback(root, true);
      return;
    }

    try {
      const googleMaps = await loadGoogleMapsApi(config.apiKey);
      const result = await geocodeAddress(googleMaps, config.address);
      const location = result.geometry.location;
      const palette = getActivePalette();
      const map = new googleMaps.Map(canvas, {
        center: location,
        zoom: 14,
        styles: buildMapStyles(palette),
        gestureHandling: "cooperative",
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        backgroundColor: palette.bg,
      });

      const marker = new googleMaps.Marker({
        map,
        position: location,
        title: config.storeName || config.mapTitle || config.address,
        icon: buildMarkerIcon(googleMaps, palette),
      });

      const resizeObserver =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(() => {
              googleMaps.event.trigger(map, "resize");
              map.setCenter(location);
            })
          : null;

      const themeObserver = new MutationObserver(() => {
        applyMapTheme(map, marker, googleMaps);
      });

      resizeObserver?.observe(canvas);
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-brand", "data-theme-scheme"],
      });

      root.__footerStorefrontMapResizeObserver = resizeObserver;
      root.__footerStorefrontMapThemeObserver = themeObserver;
      showMapFallback(root, false);
      guardRenderedMap(root, canvas);
    } catch (error) {
      console.error("Footer storefront map could not render", error);
      showMapFallback(root, true);
    }
  }

  function initFooterStorefrontMaps(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((mapRoot) => {
      if (mapRoot.dataset.footerStorefrontMapReady === "true") return;

      mapRoot.dataset.footerStorefrontMapReady = "true";
      hydrateFooterMap(mapRoot);
    });
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => initFooterStorefrontMaps());
    } else {
      initFooterStorefrontMaps();
    }

    document.addEventListener("shopify:section:load", (event) => {
      initFooterStorefrontMaps(event.target);
    });
  }
})();
